import { DiContainer, Injectable, InjectByToken } from '@jack-henry/frontend-utils/di';
import { exists, isNonEmptyString, SessionStorageService, WindowService } from '@treasury/utils';
import {
    AppType,
    AppTypeToken,
    BACKOFFICE_API,
    BuildType,
    BuildTypeToken,
    CHANNEL_API,
    Environment,
    InjectedWindow,
    INSTITUTION_ID,
} from './configuration.types';

// this code exists because Vite does not support CommonJS imports in build mode
// even with the viteCommonjs plugin that supports it in dev server;
// for now, this is a stopgap that simply appears to have no vanity URLs
// TODO: add plugin that supports CommonJS to Vite config
let vanityUrlMap = new Map<string, string>();
try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { vanityUrlToIdMap } = require('./vanity-url-map');
    vanityUrlMap = vanityUrlToIdMap;
} catch {
    // eslint-disable-next-line no-console
    console.warn(
        'Could not load vanity URL map because require was not defined. Defaulting to empty map.'
    );
}

/**
 * Service exposing information used for bootstrapping or
 * otherwise configuring channel and BO applications.
 */
@Injectable()
export class ConfigurationService {
    constructor(
        @InjectByToken(AppTypeToken) public readonly app: AppType,
        @InjectByToken(BuildTypeToken) public readonly buildType: BuildType,
        private readonly window: WindowService,
        private readonly session: SessionStorageService
    ) {
        if (app === AppType.Unknown) {
            throw new Error('Could not determine application type.');
        }
    }

    /**
     * Convenience method for locations where DI isn't available.
     */
    public static async getInstance() {
        return (await DiContainer.getInstance()).get(ConfigurationService);
    }

    public readonly uisRedirectUrl = this.generateSsoCallbackUrl;

    /**
     * The API path used when the client is deployed together with the API on the same domain.
     */
    private readonly apiPath = this.app === AppType.Channel ? CHANNEL_API : BACKOFFICE_API;

    public get institutionId() {
        const path = this.window.location.pathname.split('/').filter(isNonEmptyString);
        if (!path) {
            throw new Error('Incomplete path. Unable to determine institutionId.');
        }
        if (this.app === AppType.Channel) {
            return path[0];
        }
        if (this.app === AppType.Pwa) {
            return path[1];
        }
        if (this.app === AppType.BackOffice) {
            return this.session.get(INSTITUTION_ID) ?? '';
        }
        return '';
    }

    public set institutionId(value: string) {
        if (exists(value)) {
            this.session.set(INSTITUTION_ID, value);
        }
    }

    /**
     * The root URL prepended to all calls made to API endpoints.
     */
    public get apiRoot() {
        const { apiRoot } = this.injectedConfig;

        return isNonEmptyString(apiRoot) ? apiRoot : `${this.baseUrl}/${this.apiPath}`;
    }

    public get analyticsToken() {
        return this.injectedConfig.analyticsToken;
    }

    public get environment() {
        if (this.injectedMetadata.isLocal) {
            return Environment.Local;
        }

        const { hostname } = this.window.location;
        if (hostname) {
            const vanityUrls = Array.from(vanityUrlMap.keys());
            if (vanityUrls.some(url => hostname === url)) {
                return Environment.Production;
            }

            switch (hostname) {
                case '127.0.0.1':
                case 'localhost':
                    return Environment.Local;
                case 'treasury.dev.jackhenry.com':
                case 'treasurybackoffice.dev.jackhenry.com':
                    return Environment.Dev;
                case 'treasury.stg.jackhenry.com':
                case 'treasurybackoffice.stg.jackhenry.com':
                    return Environment.Staging;
                case 'treasury.jackhenry.com':
                case 'treasurybackoffice.jackhenry.com':
                    return Environment.Production;
                default:
                    return Environment.Unknown;
            }
        }

        return Environment.Unknown;
    }

    /**
     * The client-side ID token used by the feature flag SDK.
     */
    public get ffToken() {
        // maps an `Environment` to a LD Client-side ID
        // https://docs.launchdarkly.com/sdk/concepts/client-side-server-side#client-side-id
        switch (this.environment) {
            case Environment.Production:
                return '6633bf61801694108e04e3c2';
            case Environment.Dev:
                return '6633c04c1a652f0fc78187eb';
            case Environment.Local:
                return '665e22a09e41bb108d27a538';
            case Environment.Staging:
                return '66858f281005aa0f93eacc22';
            default:
                throw new Error('Could not retrieve LD SDK token.');
        }
    }

    public get loggingToken() {
        return this.injectedConfig.loggingToken;
    }

    public get remoteDepositToken() {
        // maps an `Environment` to a Remote Deposit SDK token
        switch (this.environment) {
            case Environment.Production:
            case Environment.Dev:
            case Environment.Staging:
                // case Environment.Local:
                return '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';
            case Environment.Local:
                return 'eyJzaWduYXR1cmUiOiJRd0x2QzdlNEZ1eHp0UVg5YkwrM0dZR21KK1pQQmY4YUJsM3E4amtLSitlUzNBd3ZYeFhkaE9jcE9FLzY1Q1o5MXJuUlcrNmFZNWtYRkFHNFVzOWVOSlpUYVlrQnZzaXZLZjF5U1E3T0ZFUFdsMEdoNU44NW9uV2JVMlErYkJKaFY2bXNlb0dKM3dtT01Xc1hCNjlBb240RTE3TGRHdzNJMmJiWUgxZkxNOE9sVjhtc3U3WHlnUnhkcVBrMDROZlp0cFBGR0h2bEVWbjF5TEZzRVZVUmZCaXhZM1BTK0g5YzU3SnAxVWFHWDJFdU05S3g0c05rbkc5SkVRZEF3cGVnSmtKODV2QTdua1prUDNDbFdxaTlZSW5Md2pmQ0VVSHFtWnl3L0gxbmFTeUxKUURoNHdGbkJmbFZieGpXblFPV1c5djBJREQva29aVFVIR1V0di9TOUE9PSIsInNpZ25lZCI6eyJleHBpcnkiOiIyMDI2LTA3LTMxIiwiZmVhdHVyZXMiOlsiZGVwb3NpdCIsImVtIl0sImFwcGxpY2F0aW9uSWRlbnRpZmllciI6eyJ3ZWIiOlsiMTAuMC4xLjE1IiwiMTkyLjE2OC4xLjE1Il19LCJncG8iOiI0NDciLCJvcmdhbml6YXRpb24iOiJNaXRlayBTeXN0ZW1zIEluYy4iLCJ2ZXJzaW9uIjoiMS4wIn19';
            default:
                throw new Error('Could not retrieve RDC SDK token.');
        }
    }

    public get version() {
        return this.injectedMetadata.version;
    }

    private get baseUrl() {
        const { protocol, hostname, port } = window.location;
        let url = `${protocol}//${hostname}`;

        if (port) {
            url += `:${port}`;
        }

        return url;
    }

    private get generateSsoCallbackUrl() {
        let { host } = window.location;
        if (this.app === AppType.Pwa) {
            host = `${host}/pwa`;
        }
        return `${window.location.protocol}//${host}/${this.institutionId}/login/sso`;
    }

    private get injectedConfig() {
        return (window as unknown as InjectedWindow).config ?? {};
    }

    private get injectedMetadata() {
        return (window as unknown as InjectedWindow).treasury ?? {};
    }
}

const buildType = BuildTypeToken.getValue() || BuildType.Unknown;

/**
 * @deprecated Prefer referencing `apiRoot` through the configuration service.
 */
export const channelApiRoot = new ConfigurationService(
    AppType.Channel,
    buildType,
    new WindowService(),
    new SessionStorageService()
).apiRoot;

/**
 * @deprecated Prefer referencing `apiRoot` through the configuration service.
 */
export const boApiRoot = new ConfigurationService(
    AppType.BackOffice,
    buildType,
    new WindowService(),
    new SessionStorageService()
).apiRoot;
